//  ----------------------------------------------------------------------------
//  Dependencies
//  ----------------------------------------------------------------------------
//  -- Components ---------------------
// -- CSS ------------------------------
//  -- Features -----------------------
import './bingo.css';
import React, { useMemo, useState } from 'react';
import styled, { css, keyframes } from 'styled-components';
//  -- Constants ----------------------

import {AddCircleSharp, CheckCircleOutline} from '@mui/icons-material';
import { Button as AntdButton, Checkbox, Dropdown, Input, Modal as ModalDrop } from 'antd';
import Loading from 'components/Loading';
import { find } from 'lodash';
import { useDispatch } from 'react-redux';
import { handleCreateOneWayBingoSession, handleUpdateOneWayBingo } from 'store/bingo/thunks';
import { useCanvasAssignmentCategory } from 'store/canvas/selectors';
import { useSchoologyGradeCategory } from 'store/schoologyauth/selectors';
import { playForFunIcon, playForGradeIcon, DebateLftIcon } from 'utils/assets/images';
import { GRADE_TYPE_ALL, GRADE_TYPE_CANVAS } from 'utils/config/reference';
import SentryWrap from 'components/Sentry';

const GradeConfig = ({ options = [], value, label, onChange, hasInput, objValue = {}, setError, is_schoology }) => {
    const label_txt = useMemo(() => {
        const f_value = value && Array.isArray(options) ? find(options, k => k.value == value || k.id == value) : null;
        if (f_value) {
            return `${label}(${f_value.title || f_value.type})`
        }
        return label;
    }, [value, options, label])

    const handleMenuClick = (e) => {
        const f_value = Array.isArray(options) ? find(options, k => k.value == e.key || k.id == e.key) : null;
        if (f_value) {
            if (f_value.value === "points") {
                onChange({ ...f_value, max_point: 100 })
                setError(false);
            } else {
                setError(false);
                onChange(f_value)
            }

        }
    };

    const handleInputChange = (e) => {
        const value = parseInt(e.target.value, 10);
        if (isNaN(value) || value <= 0) {
            setError(true);
        } else {
            setError(false);
            onChange({ ...objValue, max_point: value });
        }
        
      };

    const menuItems = Array.isArray(options) ? options.map(pt => ({
        key: pt.id || pt.value,
        label: pt.title || pt.type
    })) : [];

    const menu = {
        items: menuItems,
        onClick: handleMenuClick
    };
    return (
        <div className='linksSectionIcon text-dark' style={{ flexDirection: "column" }}>
            <Dropdown 
                menu={menu}
                trigger={['click']}
                placement="bottomLeft"
                arrow
            >
                <div className='linksSectionIcon text-dark'>
                    {value ? (
                        <span>
                            <CheckCircleOutline style={{ color: 'green' }} />
                        </span>
                    ) : (
                        <span>
                            <AddCircleSharp />
                        </span>
                    )}
                    {label_txt}
                </div>
            </Dropdown>
            {hasInput && (value === "points"|| is_schoology) && <Input placeholder="max points" defaultValue={"100"} inputMode="numeric" style={{ borderRadius: 20 }} type="numeric" onChange={handleInputChange}/>}
        </div>
    );
};

const CardSpan = styled.span`
    text-align: center;
    display: flex;
    width: 257px;
    font-weight: 600;
    font-size: 18px;
    line-height: 24px;
    padding: 0px 14px;
    margin: 0;
    cursor: pointer;
`;

const AnimatedLink = styled.a`
    display: flex;
    justify-content: space-around;
    align-items: center;
    width: 100% !important;
    border-radius: 45px;
    text-decoration: none;
    padding: 12px 12px;
    background: transparent;
    transition: background 0.5s ease;
    margin-bottom: 26px;
    box-shadow: -1px 0px 5px 0px rgb(0 0 0 / 28%);
    color:${p => p.isLoading ? "#fff" : "#000000"};
    ${({ isLoading }) => isLoading && css`
    animation: ${loadingEffect} 3s ease infinite;
    background-image: linear-gradient(to right, #3c1661, #fb00e4);
    background-size: 200% 200%;
     `}
    &:hover {
        background-image: linear-gradient(to right, #3c1661, #fb00e4);
        background-size: 200% 200%;
        color: #ffffff;
      }
`;

const CardLabel = styled.label`
    display: block;
    margin: 0;
    cursor: pointer;
`;

const loadingEffect = keyframes`
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
`;

const CardImg = styled.img`
    height: 70px;
`;

//  ----------------------------------------------------------------------------
//  Feature
//  ----------------------------------------------------------------------------
//
//  Ebook
//
//  -- Description
//  Ebook display component
//
//  -- Props
//  ...
//
//  -- State
//  ...
//
//  -- Methods
//  ...
//
//  -- Usage
//  ...
//

const GradeModal = ({
    currentUser,
    currentClass,
    onPlayGame = () => null,
    playConfigModal,
    setPlayConfigModal,
    selectedGame, setSelectedGame
}) => {
    const dispatch = useDispatch();
    const isCanvasUser = !!(currentUser && currentUser.canvas_id);
    const isSchoologyUser = !!(currentUser && currentUser.schoologyid);

    const [game_config_enable, setPlayConfigEnable] = useState(false)

    const [game_grade_category, setGameGradeCategory] = useState({});
    const [game_grade_type, setGameGradeType] = useState({});
    const [game_ungraded, setGameUngraded] = useState(false);

    const [, , schoologyAllClassGradeCategory] = useSchoologyGradeCategory();
    const canvasAssignmentCategory = useCanvasAssignmentCategory();

    const [startGameLoading, setStartGameLoading] = useState(false);
    const [gameInitLoader, setGameInitLoader] = useState(false);
    const [error, setError] = useState(false);

    const grade_type = useMemo(() => {
        if (currentClass.canvas_id) {
            return GRADE_TYPE_CANVAS;
        } else {
            return GRADE_TYPE_ALL;
        }
    }, [currentClass]);

    const grade_category = useMemo(() => {
        if (currentClass.canvas_id) {
            const grade_cat = canvasAssignmentCategory[currentClass.canvas_id];
            if (Array.isArray(grade_cat)) {
                return grade_cat.map(k => ({
                    id: k.id,
                    title: k.name
                }))
            }
            return []
        } else if (currentClass.sectionId) {
            const grade_cat = schoologyAllClassGradeCategory[currentClass.sectionId];
            if (Array.isArray(grade_cat)) {
                return grade_cat.map(k => ({
                    id: k.id,
                    title: k.title,
                    grade_type: k.calculation_type == 2 ? 'points' : 'percent'
                }))
            }
            return []
        }
        return [];
    }, [currentClass, schoologyAllClassGradeCategory, canvasAssignmentCategory])

    const diable_start = useMemo(() => {
        if (isCanvasUser) {
            return !game_grade_type.value || !game_grade_category.id
        } else if (isSchoologyUser) {
            return !game_grade_category.id
        } else if (game_ungraded) {
            return false
        } else if (error) {
            return true;
        }
        return !game_grade_type.value
    }, [game_grade_category, game_grade_type, game_ungraded, currentUser, error])

    const onPlayConfigModalClose = () => {
        setPlayConfigModal(false);
        setStartGameLoading(false);
        setPlayConfigEnable(false);
        setSelectedGame({});
        setGameGradeType({});
        setGameGradeCategory({});
        setGameUngraded(false);
        setError(false);
    }

    const onStartGame = async (gameData, play_for) => {
        const grade_config = { grade_category: game_grade_category, grade_type: game_grade_type, ungraded: game_ungraded }
        const newGame = {
            ...gameData,
            configurations: {
                ...gameData?.configurations,
                grade: play_for === 'grade' ? true : false,
                grade_config: play_for === 'grade' ? grade_config : {}
            }
        }
        setGameInitLoader(true);
        const result = await dispatch(handleUpdateOneWayBingo(newGame));
        newGame.class_id = currentClass.id;
        const result_session = await dispatch(handleCreateOneWayBingoSession(newGame));
        if (result_session?.id) {
            await onPlayGame(result_session);
        }
        setGameInitLoader(false);
        onPlayConfigModalClose()
    }

    return (
        <SentryWrap>
        <ModalDrop
            open={playConfigModal}
            onCancel={onPlayConfigModalClose}
            className={`scenarioMdlWrap`}
            maskClosable={false}
            footer={null}
        >
            <div>
                <div className='aiGmMdlInr'>
                    <div className='aiMdlLbl'><label>{game_config_enable ? "Lotería!" : "Play Lotería mode!"}</label></div>
                    {game_config_enable ?
                        <div>
                            <div className='aiGmMdlInr'>
                            <div className="createStudyBackBtn" onClick={() => {
                                setPlayConfigEnable(false);
                            }}>
                                <img src={DebateLftIcon} alt="Debate" />
                            </div>
                                {!isSchoologyUser &&
                                    <GradeConfig
                                        label={"Choose grading system"}
                                        setError={setError}
                                        value={game_grade_type?.value}
                                        options={grade_type}
                                        onChange={(value) => {
                                           setGameGradeType(value);
                                           setGameUngraded(false); // Reset ungraded when a grading system is selected
                                        }}
                                        hasInput={isSchoologyUser}
                                        objValue={game_grade_type}
                                        is_schoology={isSchoologyUser}
                                    />
                                }
                                {isCanvasUser || isSchoologyUser ?
                                    <GradeConfig
                                        label={" Choose category"}
                                        setError={setError}
                                        value={game_grade_category?.id}
                                        options={grade_category}
                                        onChange={(value) => {
                                            setGameGradeCategory(value);
                                            setGameUngraded(false); // Reset ungraded when a category is selected
                                        }}
                                        objValue={game_grade_category}
                                        hasInput={isSchoologyUser}
                                        is_schoology={isSchoologyUser}
                                    /> 
                                : null}
                                {!isSchoologyUser && !isCanvasUser ? <div className='linksSectionIcon text-dark'>
                                    <Checkbox checked={game_ungraded} className="custom-checkbox" style={{ fontSize: 16, fontWeight: "600" }} onChange={() => {
                                        setGameGradeType({});
                                        setGameGradeCategory({});
                                        setGameUngraded(!game_ungraded)
                                        setError(false);
                                    }}>
                                        {"Ungraded"}
                                    </Checkbox>
                                </div> : null}
                                <AntdButton disabled={diable_start} type="primary" shape="round" size={"large"} loading={startGameLoading} block onClick={() => {
                                    if (gameInitLoader) return;
                                    setStartGameLoading(true)
                                    onStartGame(selectedGame, 'grade')
                                }}>
                                    Play
                                </AntdButton>
                            </div>
                        </div>
                        : <div>
                            <div className='aiMdlOvr'><AnimatedLink isLoading={startGameLoading} onClick={() => {
                                if (gameInitLoader) return;
                                setStartGameLoading(true)
                                onStartGame(selectedGame, 'fun')
                            }}>
                                <CardSpan isLoading={startGameLoading}>{startGameLoading ? <Loading size={18} /> : null} Play for fun</CardSpan>
                                <CardImg src={playForFunIcon} alt="" /></AnimatedLink>
                            </div>
                            <div className='aiMdlOvr'><AnimatedLink onClick={() => {
                                if (gameInitLoader) return;
                                setPlayConfigEnable(true);
                            }}>
                                <CardSpan isLoading={startGameLoading}>
                                    <CardLabel > Play for grade</CardLabel>
                                </CardSpan>
                                <CardImg src={playForGradeIcon} alt="" /></AnimatedLink>
                            </div>
                        </div>
                    }
                </div>
            </div>
        </ModalDrop>
        </SentryWrap>
    );
};

export default GradeModal;
